<script setup lang="ts">
	import { useUsersCompanyStore } from '@/stores/usersCompany'
	import { useDebounceFn } from '@vueuse/core'
	import { storeToRefs } from 'pinia'
	import {
		type ComputedRef,
		computed,
		nextTick,
		ref,
		toRefs,
		watch,
		watchEffect,
	} from 'vue'

	import HasPermission from '@/components/HasPermission.vue'
	import SlideOver from '@/components/SlideOver.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'
	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import { useAuth } from '@/composables/useAuth'
	import useAuthUser from '@/composables/useAuthUser'

	import { CompanyGroup } from '@/interfaces/companyGroup'
	import { Department } from '@/interfaces/department'

	import useCompany from '@/modules/companies/composables/useCompany'
	import { Company } from '@/modules/companies/interfaces/company'
	import { Role } from '@/modules/roles/composables/useRoles'

	import useUsersCompany from '../composables/useUsersCompany'
	import UserFiltersActive from './UserFiltersActive.vue'

	interface Props {
		refetchingUser?: boolean
		companies: Company[]
		roles: Role[]
		groups: CompanyGroup[]
		departments: Department[]
		gridClass: string
		isIndex: boolean
		loadingDepartments?: boolean
		loadingGroups?: boolean
		loadingRoles?: boolean
		openSlideOver?: boolean
	}

	interface Emits {
		(e: 'update:company', filters: number | undefined): boolean
		(e: 'searchCompany', value: string): void
		(e: 'toggleFilter', value: boolean): void
		(e: 'update:close-slide-over'): void
		(e: 'update:save-slide-over'): void
		// ELIMINADO: (e: 'searchParams'): void - Ya no necesario
	}

	const props = withDefaults(defineProps<Props>(), {
		gridClass: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
		isIndex: false,
		refetchingUser: false,
		loadingDepartments: false,
		loadingGroups: false,
		loadingRoles: false,
		openSlideOver: false,
	})

	const emits = defineEmits<Emits>()

	const { user } = useAuthUser()
	const { company, setCompany } = useCompany()
	const { hasPermissionUser, abilities } = useAuth()

	// Mock temporal para testing - simular permisos
	const mockHasPermission = (permission: string, entityType: string) => {
		return true // Simular que el usuario tiene todos los permisos
	}

	// Usar el store de filtros directamente desde useUsersCompany
	const usersCompanyStore = useUsersCompanyStore()
	const { filters: params } = storeToRefs(usersCompanyStore)

	// Computed para la empresa seleccionada
	const selectedCompanyData = computed(() => {
		if (params.value.company) {
			return props.companies.find((c) => c.id === params.value.company)
		}
		return undefined
	})

	// Computed refs para mapear datos
	const rolesMap: ComputedRef<Role[]> = computed(() => {
		return props.roles || []
	})

	const groups = computed(() => {
		return props.groups || []
	})

	const departments: ComputedRef<Department[]> = computed(() => {
		return props.departments || []
	})

	// Computed refs para filtros individuales usando el store
	const filterCompanyId = computed({
		get: () => params.value.company,
		set: (value) => usersCompanyStore.setFiltersValue('company', value),
	})

	const filterUsername = computed({
		get: () => params.value.search,
		set: (value) => usersCompanyStore.setFiltersValue('search', value),
	})

	const filterState = computed({
		get: () => params.value.status,
		set: (value) => {
			usersCompanyStore.setFiltersValue('status', value)
		},
	})

	const filterRole = computed({
		get: () => params.value.role,
		set: (value) => usersCompanyStore.setFiltersValue('role', value),
	})

	const filterGroup = computed({
		get: () => params.value.group,
		set: (value) => usersCompanyStore.setFiltersValue('group', value),
	})

	const filterDepartment = computed({
		get: () => params.value.department,
		set: (value) => usersCompanyStore.setFiltersValue('department', value),
	})

	// Filtro "Supervisado por": el combobox vive en el hero de IndexPage
	// (SupervisorFilter.vue); aquí solo se necesita el objeto para el chip.
	const { supervisorUser } = storeToRefs(usersCompanyStore)

	const filterRoleChange = (role: string | null | undefined) => {
		console.log('filterRoleChange', role)
		// Asegurar que null se mantenga como null
		filterRole.value = role === null ? null : role
	}

	const filterGroupChange = (group: number | null | undefined) => {
		// Asegurar que null se mantenga como null
		filterGroup.value = group === null ? null : group
	}

	const filterDepartmentChange = (department: number | null | undefined) => {
		// Asegurar que null se mantenga como null
		filterDepartment.value = department === null ? null : department
	}

	const filterStatusChange = (status: string | null | undefined) => {
		// Asegurar que null se mantenga como null
		filterState.value = status === null ? null : status
	}

	const filterOrderBy = computed({
		get: () => params.value.sortBy,
		set: (value) => {
			usersCompanyStore.setFiltersValue('sortBy', value)
		},
	})

	const filterOrderDirection = computed({
		get: () => params.value.sortDirection,
		set: (value) => {
			usersCompanyStore.setFiltersValue('sortDirection', value)
		},
	})

	// Función para manejar cambios en departamentos
	const handleDepartmentChange = (companyId: number | undefined) => {
		if (companyId !== filterCompanyId.value) {
			filterDepartment.value = undefined
		}
	}

	// Watcher para departamentos
	watchEffect(() => {
		handleDepartmentChange(filterCompanyId.value)
	})

	// Función para resetear filtros
	const handleResetFilters = () => {
		// Resetear usando el store unificado (limpia también supervisorUser)
		usersCompanyStore.resetFilters()

		// Forzar la actualización de la empresa seleccionada
		setCompany(undefined)
		emits('update:company', undefined)
		// ELIMINADO: emits('searchParams') - Ya no necesario, el watcher de useUsersCompany se encarga

		// Forzar re-renderizado después de un tick
		nextTick(() => {})
	}

	// Función para cerrar el slide over
	const closeSlideOver = () => {
		emits('update:close-slide-over')
	}

	// Función para guardar filtros
	const saveFilters = () => {
		emits('update:save-slide-over')
	}

	// Función para manejar búsqueda de empresa
	const handleSearchCompany = (value: string | Event) => {
		const searchValue =
			typeof value === 'string' ? value : (
				(value.target as HTMLInputElement)?.value || ''
			)
		emits('searchCompany', searchValue)
	}

	// Función para manejar el input de búsqueda con el tipo correcto
	const handleSearchInput = (event: Event) => {
		const target = event.target as HTMLInputElement
		const searchValue = target.value

		// Actualizar el store unificado
		usersCompanyStore.setFiltersValue('search', searchValue)
		usersCompanyStore.setFiltersValue('status', null)
		usersCompanyStore.setPage('1')
	}

	// Función para seleccionar empresa
	const selectedCompany = (companySelect: Company) => {
		usersCompanyStore.setFiltersValue('company', companySelect.id)
		setCompany(companySelect)
		emits('update:company', companySelect.id)
	}

	// Función para manejar cambio de empresa seleccionada desde UserFiltersActive
	const handleSelectedCompany = (company: Company | undefined) => {
		if (company) {
			selectedCompany(company)
		} else {
			usersCompanyStore.setFiltersValue('company', undefined)
			emits('update:company', undefined)
		}
	}

	// Mapas para selects
	const statusOptions = [
		{ value: null, label: 'Todos' },
		{ value: 'active', label: 'Activo' },
		{ value: 'inactive', label: 'Inactivo' },
	]

	const roleOptions = computed(() => {
		return [
			{ value: null, label: 'Todos los roles' },
			...rolesMap.value.map((role) => ({ value: role.name, label: role.name })),
		]
	})

	const groupOptions = computed(() => {
		return [
			{ value: null, label: 'Todos los grupos' },
			...groups.value.map((group) => ({ value: group.id, label: group.name })),
		]
	})

	const departmentOptions = computed(() => {
		return [
			{ value: null, label: 'Todos los departamentos' },
			...departments.value.map((dept) => ({
				value: dept.id,
				label: dept.name,
			})),
		]
	})

	const sortByOptions = [
		{ value: 'name', label: 'Nombre' },
		{ value: 'email', label: 'Email' },
		{ value: 'status', label: 'Estatus' },
		{ value: 'created_at', label: 'Fecha de creación' },
		{ value: 'group', label: 'Grupo' },
		{ value: 'department', label: 'Departamento' },
	]

	const sortDirectionOptions = [
		{ value: null, label: 'Por defecto' },
		{ value: 'asc', label: 'Ascendente' },
		{ value: 'desc', label: 'Descendente' },
	]
</script>

<template>
	<SlideOver
		:entity-type="'users'"
		title="Filtros de Usuarios"
		:when="openSlideOver"
		@update:close-slide-over="
			() => {
				emits('toggleFilter', false)
			}
		"
		@update:save-slide-over="
			() => {
				emits('toggleFilter', false)
			}
		"
		:hide-buttons="true"
	>
		<template #default>
			<div class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-full">
				<!-- Header Section Mejorado con diseño idéntico a CefFilters -->
				<div class="mb-8 relative">
					<!-- Decorative background -->
					<div
						class="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl opacity-30 transform -skew-y-1"
					></div>

					<div
						class="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
					>
						<div class="flex items-center gap-4 mb-3">
							<div class="p-3 bg-blue-100 rounded-lg">
								<svg
									class="w-6 h-6 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
									></path>
								</svg>
							</div>
							<div>
								<h3 class="text-xl font-bold text-gray-900">
									Filtros de Usuarios
								</h3>
								<p class="text-sm text-gray-600 mt-1">
									Encuentra exactamente lo que buscas con nuestros filtros
									inteligentes
								</p>
							</div>
						</div>

						<!-- Filtros Activos - Sección prominente -->
						<UserFiltersActive
							:params="params"
							:selected-company="selectedCompanyData"
							:selected-supervisor="supervisorUser"
							:roles="roles"
							:groups="groups"
							:departments="departments"
							@reset-filters="handleResetFilters"
							@handle-selected-company="handleSelectedCompany"
							@handle-selected-role="
								(role: string | undefined) => filterRoleChange(role)
							"
							@handle-selected-group="
								(group: number | undefined) => filterGroupChange(group)
							"
							@handle-selected-department="
								(dept: number | undefined) => filterDepartmentChange(dept)
							"
							@handle-selected-supervisor="
								() => usersCompanyStore.setSupervisorUser(undefined)
							"
						/>
					</div>
				</div>

				<!-- Contenido de filtros organizado en secciones -->
				<div class="space-y-6">
					<!-- Sección: Información básica -->
					<div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-green-100 rounded-lg">
								<svg
									class="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									></path>
								</svg>
							</div>
							<h4 class="text-lg font-semibold text-gray-900">
								Información del Usuario
							</h4>
						</div>

						<div class="grid gap-4">
							<!-- Empresa (si no es usuario interno) -->
							<div
								v-if="!user?.internal"
								class="space-y-2"
							>
								<label
									class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
								>
									<div class="flex items-center gap-2">
										<svg
											class="w-4 h-4 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
											></path>
										</svg>
										Empresa
									</div>
								</label>
								<StyledCombobox
									v-model="company"
									@update:model-value="(e) => e && selectedCompany(e)"
									:options="props.companies"
									placeholder="Selecciona una compañía"
									:on-input-change="handleSearchCompany"
									:show-input="!company"
									class="w-full"
								>
									<template #default="{ option }">
										<span>{{ option.name }}</span>
									</template>

									<template #input-overlap>
										<template v-if="company">
											<p class="truncate text-sm normal-case">
												{{ `${company.name} ` }}
											</p>
											<button
												class="material-symbols-outlined text-[18px]"
												@click="setCompany(undefined)"
											>
												close
											</button>
										</template>
									</template>
								</StyledCombobox>
								<p class="text-xs text-gray-500 mt-1">
									Empresa a la que pertenece el usuario
								</p>
							</div>

							<!-- Búsqueda por nombre -->
							<div class="space-y-2">
								<label
									class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
								>
									<div class="flex items-center gap-2">
										<svg
											class="w-4 h-4 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
											></path>
										</svg>
										Nombre de usuario
										<LoadingSpin
											v-if="refetchingUser && params.search"
											class="h-4 w-4 animate-spin text-gray-500"
											:dark="true"
										/>
									</div>
								</label>
								<input
									v-model="filterUsername"
									@input="handleSearchInput"
									placeholder="Ingresa nombre del usuario"
									type="text"
									name="search"
									class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
								/>
								<p class="text-xs text-gray-500 mt-1">
									Buscar por nombre completo del usuario
								</p>
							</div>

							<!-- Estado -->
							<div class="space-y-2">
								<label
									class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
								>
									<div class="flex items-center gap-2">
										<svg
											class="w-4 h-4 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											></path>
										</svg>
										Estatus del usuario
									</div>
								</label>
								<StyledSelect
									v-model="filterState"
									:options="statusOptions"
									v-on:update:model-value="(e: string) => filterStatusChange(e)"
									placeholder="Selecciona un estado"
									class="w-full"
								/>
								<p class="text-xs text-gray-500 mt-1">
									Estatus actual del usuario en el sistema
								</p>
							</div>
						</div>
					</div>

					<!-- Sección: Filtros organizacionales -->
					<div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-purple-100 rounded-lg">
								<svg
									class="w-5 h-5 text-purple-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
									></path>
								</svg>
							</div>
							<h4 class="text-lg font-semibold text-gray-900">Organización</h4>
						</div>

						<div class="grid gap-4">
							<!-- Rol -->
							<HasPermission
								:entity-type="'App\\Models\\Company'"
								name-permission="view-roles"
							>
								<div class="space-y-2">
									<label
										class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
									>
										<div class="flex items-center gap-2">
											<svg
												class="w-4 h-4 text-blue-600"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
												></path>
											</svg>
											Rol del usuario
										</div>
									</label>
									<StyledSelect
										v-model="filterRole"
										:options="roleOptions"
										v-on:update:model-value="(e: string) => filterRoleChange(e)"
										placeholder="Selecciona un rol"
										class="w-full"
										:loading-options="loadingRoles"
									/>
									<p class="text-xs text-gray-500 mt-1">
										Rol asignado al usuario en la empresa
									</p>
								</div>
							</HasPermission>

							<!-- Grupo -->
							<HasPermission
								name-permission="view-groups"
								:entity-type="'App\\Models\\Company'"
							>
								<div class="space-y-2">
									<label
										class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
									>
										<div class="flex items-center gap-2">
											<svg
												class="w-4 h-4 text-blue-600"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
												></path>
											</svg>
											Grupo de trabajo
										</div>
									</label>
									<StyledSelect
										v-model="filterGroup"
										:options="groupOptions"
										v-on:update:model-value="
											(e: number) => filterGroupChange(e)
										"
										placeholder="Selecciona un grupo"
										class="w-full"
										:loading-options="loadingGroups"
									/>
									<p class="text-xs text-gray-500 mt-1">
										Grupo de trabajo al que pertenece el usuario
									</p>
								</div>
							</HasPermission>

							<!-- Departamento -->
							<HasPermission
								name-permission="view-departments"
								:entity-type="'App\\Models\\Company'"
							>
								<div class="space-y-2">
									<label
										class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
									>
										<div class="flex items-center gap-2">
											<svg
												class="w-4 h-4 text-blue-600"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
												></path>
											</svg>
											Departamento
										</div>
									</label>
									<StyledSelect
										v-model="filterDepartment"
										:options="departmentOptions"
										v-on:update:model-value="
											(e: number) => filterDepartmentChange(e)
										"
										placeholder="Selecciona un departamento"
										class="w-full"
										:loading-options="loadingDepartments"
									/>
									<p class="text-xs text-gray-500 mt-1">
										Departamento al que está asignado el usuario
									</p>
								</div>
							</HasPermission>

						</div>
					</div>

					<!-- Sección: Ordenamiento -->
					<div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-orange-100 rounded-lg">
								<svg
									class="w-5 h-5 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
									></path>
								</svg>
							</div>
							<h4 class="text-lg font-semibold text-gray-900">Ordenamiento</h4>
						</div>

						<div class="grid gap-4">
							<!-- Ordenar por -->
							<div class="space-y-2">
								<label
									class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
								>
									<div class="flex items-center gap-2">
										<svg
											class="w-4 h-4 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
											></path>
										</svg>
										Ordenar por
									</div>
								</label>
								<StyledSelect
									v-model="filterOrderBy"
									:options="sortByOptions"
									placeholder="Selecciona criterio de orden"
									class="w-full"
								/>
								<p class="text-xs text-gray-500 mt-1">
									Campo por el cual ordenar los resultados
								</p>
							</div>

							<!-- Dirección de orden -->
							<div
								v-if="filterOrderBy !== 'all'"
								class="space-y-2"
							>
								<label
									class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
								>
									<div class="flex items-center gap-2">
										<svg
											class="w-4 h-4 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
											></path>
										</svg>
										Dirección de orden
									</div>
								</label>
								<StyledSelect
									v-model="filterOrderDirection"
									:options="sortDirectionOptions"
									placeholder="Selecciona dirección"
									class="w-full"
								/>
								<p class="text-xs text-gray-500 mt-1">
									Dirección del ordenamiento de los resultados
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<div
				class="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200"
			>
				<!-- Botones de acción mejorados -->
				<div class="flex items-center justify-center gap-4 p-4 w-full">
					<button
						@click.prevent="handleResetFilters"
						class="group inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
					>
						<svg
							class="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							></path>
						</svg>
						<span>Limpiar filtros</span>
					</button>
				</div>
			</div>
		</template>
	</SlideOver>
</template>

<style scoped></style>
